import { UserRole } from '@nebalkon-workspace/auth';
import {
  OrderResponse,
  OrderStatuses,
  OrderStatus,
  OrderTypes,
  OrderType,
} from '@nebalkon-workspace/order-api';

/**
 * Snapshot of staff-order detail page behaviour as of the pre-cockpit redesign.
 * Source of truth for backend rules: `apps/api/internal/order/model/order_status.go`.
 * Legacy UI flags mirror `StaffOrderDetailComponent` computed signals.
 */

export type StaffOrderStaffRole = 'manager' | 'mover' | 'warehouse_worker';

/** Role branch priority in `showOrderWorkspace` (first match wins). */
export const STAFF_ORDER_WORKSPACE_ROLE_PRIORITY: readonly StaffOrderStaffRole[] =
  ['mover', 'manager', 'warehouse_worker'];

export type StaffOrderActionId =
  | 'take_in_progress'
  | 'agree_pending'
  | 'assign_movers'
  | 'cancel'
  | 'complete'
  | 'confirm_payment'
  | 'edit_order'
  | 'add_note'
  | 'manage_order_items'
  | 'confirm_client_visit'
  | 'mover_start'
  | 'mover_transit'
  | 'mover_delivered'
  | 'mover_accept_box'
  | 'mover_accept_item'
  | 'mover_inventory'
  | 'release_items_to_client'
  | 'warehouse_receive'
  | 'warehouse_handover';

export interface StaffOrderFlowVariant {
  type: OrderType;
  returnToWarehouse: boolean;
  /** Canonical forward status chain (excludes cancel). */
  statusPath: readonly OrderStatus[];
  /** Statuses from which `POST /admin/orders/:id/complete` succeeds. */
  completeFromStatuses: readonly OrderStatus[];
}

export interface StaffOrderActionDefinition {
  id: StaffOrderActionId;
  roles: readonly StaffOrderStaffRole[];
  method: string;
  path: string;
  /** When set, action is only meaningful in these order statuses (backend). */
  backendStatuses?: readonly OrderStatus[];
  notes?: string;
}

export interface StaffOrderFlowDiscrepancy {
  id: string;
  summary: string;
  backendRule: string;
  frontendRule: string;
}

/** Options mirroring Go `transitionsFor` self-logistics flags in order_status.go. */
export interface BackendStatusTransitionOptions {
  clientSelfDropoff?: boolean;
  clientSelfPickup?: boolean;
}

/** Mirrors Go `transitionsFor` in order_status.go. */
export function backendStatusTransitions(
  type: OrderType,
  returnToWarehouse: boolean,
  options: BackendStatusTransitionOptions = {},
): Readonly<Partial<Record<OrderStatus, readonly OrderStatus[]>>> {
  const { clientSelfDropoff = false, clientSelfPickup = false } = options;
  const base: Partial<Record<OrderStatus, OrderStatus[]>> = {};
  const add = (from: OrderStatus, ...to: OrderStatus[]): void => {
    base[from] = [...(base[from] ?? []), ...to];
  };

  switch (type) {
    case OrderTypes.Pickup:
      add(OrderStatuses.New, OrderStatuses.InProcess);
      add(OrderStatuses.InProcess, OrderStatuses.Pending);
      if (clientSelfDropoff) {
        add(OrderStatuses.Pending, OrderStatuses.InTransit);
      } else {
        add(OrderStatuses.Pending, OrderStatuses.InWork);
        add(OrderStatuses.InWork, OrderStatuses.InTransit);
      }
      add(OrderStatuses.InTransit, OrderStatuses.Stored);
      add(OrderStatuses.Stored, OrderStatuses.Completed);
      break;
    case OrderTypes.Delivery:
      add(OrderStatuses.New, OrderStatuses.InProcess);
      add(OrderStatuses.InProcess, OrderStatuses.Pending);
      if (clientSelfPickup) {
        add(OrderStatuses.Pending, OrderStatuses.InTransit);
      } else {
        add(OrderStatuses.Pending, OrderStatuses.InWork);
        add(OrderStatuses.InWork, OrderStatuses.InTransit);
      }
      if (returnToWarehouse) {
        add(OrderStatuses.InTransit, OrderStatuses.Stored);
        add(OrderStatuses.Stored, OrderStatuses.Completed);
      } else {
        add(OrderStatuses.InTransit, OrderStatuses.Completed);
      }
      break;
    case OrderTypes.Moving:
      add(OrderStatuses.New, OrderStatuses.InProcess);
      add(OrderStatuses.InProcess, OrderStatuses.Pending);
      add(OrderStatuses.Pending, OrderStatuses.InWork);
      add(OrderStatuses.InWork, OrderStatuses.Completed);
      break;
    default:
      break;
  }

  for (const st of [
    OrderStatuses.New,
    OrderStatuses.InProcess,
    OrderStatuses.Pending,
  ] as const) {
    add(st, OrderStatuses.Canceled);
  }

  return base;
}

export function backendCanTransition(
  type: OrderType,
  returnToWarehouse: boolean,
  from: OrderStatus,
  to: OrderStatus,
  options: BackendStatusTransitionOptions = {},
): boolean {
  if (from === to) {
    return false;
  }
  const allowed = backendStatusTransitions(type, returnToWarehouse, options)[
    from
  ];
  return allowed?.includes(to) ?? false;
}

export function backendCanCompleteOrder(
  order: Pick<OrderResponse, 'type' | 'status' | 'return_to_warehouse'> &
    Partial<Pick<OrderResponse, 'client_self_dropoff' | 'client_self_pickup'>>,
): boolean {
  return backendCanTransition(
    order.type as OrderType,
    order.return_to_warehouse,
    order.status as OrderStatus,
    OrderStatuses.Completed,
    {
      clientSelfDropoff: order.client_self_dropoff === true,
      clientSelfPickup: order.client_self_pickup === true,
    },
  );
}

/** Documented flow variants aligned with backend tests in order_workflow_test.go. */
export const BACKEND_ORDER_FLOW_VARIANTS: readonly StaffOrderFlowVariant[] = [
  {
    type: OrderTypes.Pickup,
    returnToWarehouse: false,
    statusPath: [
      OrderStatuses.New,
      OrderStatuses.InProcess,
      OrderStatuses.Pending,
      OrderStatuses.InWork,
      OrderStatuses.InTransit,
      OrderStatuses.Stored,
      OrderStatuses.Completed,
    ],
    completeFromStatuses: [OrderStatuses.Stored],
  },
  {
    type: OrderTypes.Delivery,
    returnToWarehouse: false,
    statusPath: [
      OrderStatuses.New,
      OrderStatuses.InProcess,
      OrderStatuses.Pending,
      OrderStatuses.InWork,
      OrderStatuses.InTransit,
      OrderStatuses.Completed,
    ],
    completeFromStatuses: [OrderStatuses.InTransit],
  },
  {
    type: OrderTypes.Delivery,
    returnToWarehouse: true,
    statusPath: [
      OrderStatuses.New,
      OrderStatuses.InProcess,
      OrderStatuses.Pending,
      OrderStatuses.InWork,
      OrderStatuses.InTransit,
      OrderStatuses.Stored,
      OrderStatuses.Completed,
    ],
    completeFromStatuses: [OrderStatuses.Stored],
  },
  {
    type: OrderTypes.Moving,
    returnToWarehouse: false,
    statusPath: [
      OrderStatuses.New,
      OrderStatuses.InProcess,
      OrderStatuses.Pending,
      OrderStatuses.InWork,
      OrderStatuses.Completed,
    ],
    completeFromStatuses: [OrderStatuses.InWork],
  },
];

/** Staff-facing actions and their backend entry points (current API). */
export const STAFF_ORDER_ACTION_DEFINITIONS: readonly StaffOrderActionDefinition[] =
  [
    {
      id: 'take_in_progress',
      roles: ['manager'],
      method: 'PATCH',
      path: '/admin/orders/:id',
      backendStatuses: [OrderStatuses.New],
      notes: 'status → in_process',
    },
    {
      id: 'agree_pending',
      roles: ['manager'],
      method: 'PATCH',
      path: '/admin/orders/:id',
      backendStatuses: [OrderStatuses.InProcess],
      notes: 'status → pending',
    },
    {
      id: 'assign_movers',
      roles: ['manager'],
      method: 'PUT',
      path: '/admin/orders/:id/assign',
      backendStatuses: [
        OrderStatuses.Pending,
        OrderStatuses.InWork,
        OrderStatuses.InTransit,
      ],
      notes: 'Reason is required for reassignment from in_work/in_transit',
    },
    {
      id: 'cancel',
      roles: ['manager'],
      method: 'POST',
      path: '/admin/orders/:id/cancel',
      backendStatuses: [
        OrderStatuses.New,
        OrderStatuses.InProcess,
        OrderStatuses.Pending,
      ],
    },
    {
      id: 'complete',
      roles: ['manager'],
      method: 'POST',
      path: '/admin/orders/:id/complete',
      notes:
        'Uses CanTransitionTo(completed); see BACKEND_ORDER_FLOW_VARIANTS.completeFromStatuses',
    },
    {
      id: 'confirm_payment',
      roles: ['manager'],
      method: 'POST',
      path: '/admin/orders/:id/confirm-payment',
      notes: 'Independent of order status; only checks payment_status !== paid',
    },
    {
      id: 'edit_order',
      roles: ['manager'],
      method: 'PATCH',
      path: '/admin/orders/:id',
      notes:
        'Blocked in UI for completed/canceled; backend allows non-terminal patch without status guard',
    },
    {
      id: 'mover_start',
      roles: ['mover'],
      method: 'POST',
      path: '/admin/orders/:id/start',
      backendStatuses: [OrderStatuses.Pending],
      notes: 'pending → in_work; assigned mover only',
    },
    {
      id: 'mover_transit',
      roles: ['mover'],
      method: 'POST',
      path: '/admin/orders/:id/transit',
      notes:
        'pickup/delivery: in_work → in_transit; delivery+return second leg stays in_transit; pickup requires all units accepted',
    },
    {
      id: 'mover_delivered',
      roles: ['mover'],
      method: 'POST',
      path: '/admin/orders/:id/delivered',
      backendStatuses: [OrderStatuses.InTransit],
      notes:
        'delivery without return_to_warehouse only; marks boxes delivered, does NOT complete order',
    },
    {
      id: 'warehouse_receive',
      roles: ['warehouse_worker'],
      method: 'POST',
      path: '/admin/orders/:id/receive',
      backendStatuses: [OrderStatuses.InTransit],
      notes: 'Auto-advances order to stored when all units received',
    },
    {
      id: 'confirm_client_visit',
      roles: ['manager'],
      method: 'POST',
      path: '/admin/orders/:id/confirm-client-visit',
      backendStatuses: [OrderStatuses.Pending],
      notes: 'Self logistics only: pending → in_transit',
    },
    {
      id: 'warehouse_handover',
      roles: ['warehouse_worker'],
      method: 'POST',
      path: '/admin/orders/:id/handover',
      backendStatuses: [OrderStatuses.InTransit],
      notes: 'Self pickup only: hand one box/item to client at warehouse',
    },
    {
      id: 'release_items_to_client',
      roles: ['mover'],
      method: 'POST',
      path: '/admin/orders/:id/items/release',
      backendStatuses: [OrderStatuses.InWork, OrderStatuses.InTransit],
      notes: 'delivery + return_to_warehouse only',
    },
  ];

/** Known mismatches between legacy UI gates and backend rules. */
export const STAFF_ORDER_FLOW_DISCREPANCIES: readonly StaffOrderFlowDiscrepancy[] =
  [
    {
      id: 'canComplete-too-broad',
      summary: 'Complete button shown for statuses/types backend rejects',
      backendRule:
        'Complete only when CanTransitionTo(completed): pickup/delivery+return from stored; delivery full return from in_transit; moving from in_work',
      frontendRule:
        'canComplete: manager && status in {in_work, in_transit, stored} — ignores order type',
    },
    {
      id: 'workspace-role-priority',
      summary: 'Multi-role users see mover workspace, not manager workspace',
      backendRule: 'N/A — roles are independent on API',
      frontendRule:
        'showOrderWorkspace checks isMover() first; manager composition hidden when user also has Mover role',
    },
    {
      id: 'showOrderActions-excludes-mover',
      summary: 'Manager quick actions hidden for mover-only UI branch',
      backendRule: 'Mover endpoints remain available',
      frontendRule: 'showOrderActions: manager || warehouse_worker (not mover)',
    },
    {
      id: 'mover-delivered-not-complete',
      summary: 'Delivery full return: mover "delivered" does not close order',
      backendRule:
        'MoverMarkDeliveredOrder updates box storage status only; manager must POST /complete from in_transit',
      frontendRule:
        'canMoverDelivered shown at in_transit; canComplete also shown to manager at same status',
    },
    {
      id: 'confirm-payment-in-flow',
      summary:
        'Payment confirmation is decoupled from lifecycle but prominent in UI',
      backendRule: 'ConfirmPayment anytime while payment_status !== paid',
      frontendRule:
        'canConfirmPayment: manager && payment_status !== paid — shown alongside operational actions',
    },
    {
      id: 'warehouse-receive-toggle',
      summary: 'Warehouse intake hidden until manual toggle',
      backendRule: 'POST /receive allowed whenever order is in_transit',
      frontendRule:
        'canWarehouseReceive && receivingMode() — worker must expand receiving panel first',
    },
    {
      id: 'assign-movers-reassign-reason',
      summary: 'Reassignment after work starts requires reason',
      backendRule:
        'AssignMoversTeam accepts pending/in_work/in_transit; in_work/in_transit require reason',
      frontendRule:
        'canAssignMovers: manager && status in {pending,in_work,in_transit}; reason required for in_work/in_transit',
    },
  ];

export function hasStaffRole(
  roles: readonly UserRole[],
  staffRole: StaffOrderStaffRole,
): boolean {
  switch (staffRole) {
    case 'manager':
      return roles.includes(UserRole.Admin) || roles.includes(UserRole.Manager);
    case 'mover':
      return roles.includes(UserRole.Mover);
    case 'warehouse_worker':
      return roles.includes(UserRole.WarehouseWorker);
  }
}

/** Which workspace branch `showOrderWorkspace` uses (legacy priority). */
export function resolveStaffOrderWorkspaceRole(
  roles: readonly UserRole[],
): StaffOrderStaffRole | null {
  for (const role of STAFF_ORDER_WORKSPACE_ROLE_PRIORITY) {
    if (hasStaffRole(roles, role)) {
      return role;
    }
  }
  return null;
}

type OrderFlowSlice = Pick<
  OrderResponse,
  'type' | 'status' | 'return_to_warehouse' | 'user_id' | 'warehouse_id'
>;

/** Legacy `canComplete` from StaffOrderDetailComponent. */
export function legacyFrontendCanComplete(
  order: Pick<OrderResponse, 'status'>,
  isManager: boolean,
): boolean {
  if (!isManager) {
    return false;
  }
  return (
    order.status === OrderStatuses.InWork ||
    order.status === OrderStatuses.InTransit ||
    order.status === OrderStatuses.Stored
  );
}

function legacyCanMoverStart(order: OrderFlowSlice, isMover: boolean): boolean {
  return isMover && order.status === OrderStatuses.Pending;
}

function legacyShowMoverTransitButton(
  order: OrderFlowSlice,
  isMover: boolean,
): boolean {
  if (!isMover) {
    return false;
  }
  if (order.type === OrderTypes.Moving) {
    return false;
  }
  if (order.status === OrderStatuses.InWork) {
    return (
      order.type === OrderTypes.Pickup || order.type === OrderTypes.Delivery
    );
  }
  if (
    order.status === OrderStatuses.InTransit &&
    order.type === OrderTypes.Delivery &&
    order.return_to_warehouse
  ) {
    return true;
  }
  return false;
}

function legacyCanMoverDelivered(
  order: OrderFlowSlice,
  isMover: boolean,
): boolean {
  return (
    isMover &&
    order.type === OrderTypes.Delivery &&
    !order.return_to_warehouse &&
    order.status === OrderStatuses.InTransit
  );
}

function legacyCanMoverInventory(
  order: OrderFlowSlice,
  isMover: boolean,
): boolean {
  if (!isMover || !order.user_id) {
    return false;
  }
  if (
    order.status !== OrderStatuses.InWork &&
    order.status !== OrderStatuses.InTransit
  ) {
    return false;
  }
  return (
    order.type === OrderTypes.Pickup ||
    order.type === OrderTypes.Moving ||
    order.type === OrderTypes.Delivery
  );
}

function legacyCanWarehouseReceive(
  order: OrderFlowSlice,
  isWarehouseWorker: boolean,
): boolean {
  return (
    isWarehouseWorker &&
    order.status === OrderStatuses.InTransit &&
    !!order.warehouse_id
  );
}

/** Legacy `showOrderWorkspace` from StaffOrderDetailComponent. */
export function legacyFrontendShowOrderWorkspace(
  roles: readonly UserRole[],
  order: OrderFlowSlice,
  receivingMode: boolean,
): boolean {
  const isMover = hasStaffRole(roles, 'mover');
  const isManager = hasStaffRole(roles, 'manager');
  const isWarehouseWorker = hasStaffRole(roles, 'warehouse_worker');

  if (isMover) {
    return (
      legacyCanMoverStart(order, isMover) ||
      legacyShowMoverTransitButton(order, isMover) ||
      legacyCanMoverDelivered(order, isMover) ||
      legacyCanMoverInventory(order, isMover)
    );
  }
  if (isManager) {
    return !!order.user_id;
  }
  if (isWarehouseWorker) {
    return legacyCanWarehouseReceive(order, isWarehouseWorker) && receivingMode;
  }
  return false;
}

/** Legacy `showOrderActions` from StaffOrderDetailComponent. */
export function legacyFrontendShowOrderActions(
  roles: readonly UserRole[],
): boolean {
  return (
    hasStaffRole(roles, 'manager') || hasStaffRole(roles, 'warehouse_worker')
  );
}

/** Returns discrepancy ids that apply to a concrete order + role set. */
export function activeStaffOrderFlowDiscrepancies(
  order: Pick<OrderResponse, 'type' | 'status' | 'return_to_warehouse'>,
  roles: readonly UserRole[],
): StaffOrderFlowDiscrepancy[] {
  const active: StaffOrderFlowDiscrepancy[] = [];
  const isManager = hasStaffRole(roles, 'manager');
  const pushDiscrepancy = (id: string): void => {
    const discrepancy = STAFF_ORDER_FLOW_DISCREPANCIES.find((d) => d.id === id);
    if (discrepancy) {
      active.push(discrepancy);
    }
  };

  if (
    isManager &&
    legacyFrontendCanComplete(order, true) &&
    !backendCanCompleteOrder(order)
  ) {
    pushDiscrepancy('canComplete-too-broad');
  }

  if (
    hasStaffRole(roles, 'mover') &&
    hasStaffRole(roles, 'manager') &&
    resolveStaffOrderWorkspaceRole(roles) === 'mover'
  ) {
    pushDiscrepancy('workspace-role-priority');
  }

  if (hasStaffRole(roles, 'mover') && !legacyFrontendShowOrderActions(roles)) {
    pushDiscrepancy('showOrderActions-excludes-mover');
  }

  return active;
}

/** Status/type pairs where legacy UI shows Complete but backend rejects it. */
export function legacyCompleteFalsePositiveMatrix(): Array<{
  type: OrderType;
  returnToWarehouse: boolean;
  status: OrderStatus;
}> {
  const cases: Array<{
    type: OrderType;
    returnToWarehouse: boolean;
    status: OrderStatus;
  }> = [];

  for (const variant of BACKEND_ORDER_FLOW_VARIANTS) {
    for (const status of [
      OrderStatuses.InWork,
      OrderStatuses.InTransit,
      OrderStatuses.Stored,
    ] as const) {
      const order = {
        type: variant.type,
        status,
        return_to_warehouse: variant.returnToWarehouse,
      };
      if (
        legacyFrontendCanComplete(order, true) &&
        !backendCanCompleteOrder(order)
      ) {
        cases.push({
          type: variant.type,
          returnToWarehouse: variant.returnToWarehouse,
          status,
        });
      }
    }
  }

  return cases;
}
